"use client";

import type { ConvertRequest, LanguagePreference, SupportedLanguageCode } from "@fluentry/domain";

// Keep UI-facing language labels in shared-workbench so domain stays focused on contracts and value semantics.
export const LANGUAGE_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "en", label: "English" },
  { value: "ja", label: "Japanese" },
  { value: "zh-Hans", label: "Chinese (Simplified)" },
  { value: "ko", label: "Korean" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "pt", label: "Portuguese" },
] as const satisfies ReadonlyArray<{ value: LanguagePreference; label: string }>;

type ResolvedLanguagePair = {
  nativeLanguage: SupportedLanguageCode;
  targetLanguage: SupportedLanguageCode;
};

const JAPANESE_CHAR_PATTERN = /[\u3040-\u30ff\u3400-\u9fff]/u;
const KOREAN_CHAR_PATTERN = /[\u1100-\u11ff\u3130-\u318f\uac00-\ud7af]/u;

export function toConvertRequestLanguages(preferences: {
  nativeLanguagePreference: LanguagePreference;
  targetLanguagePreference: LanguagePreference;
  followInputLanguage: boolean;
}): Pick<ConvertRequest, "nativeLanguage" | "targetLanguage"> {
  return {
    // Keep the user's explicit baseline language in the payload so restore UIs can preserve it even when follow mode is active.
    nativeLanguage: preferences.nativeLanguagePreference === "auto" ? undefined : preferences.nativeLanguagePreference,
    targetLanguage: preferences.targetLanguagePreference === "auto" ? undefined : preferences.targetLanguagePreference,
  };
}

export function resolveConvertRequestLanguages(args: {
  nativeLanguagePreference: LanguagePreference;
  targetLanguagePreference: LanguagePreference;
  followInputLanguage: boolean;
  lastResolvedLanguagePair?: ResolvedLanguagePair | null;
  input: string;
}): ResolvedLanguagePair {
  const effectiveNativeLanguagePreference = getEffectiveNativeLanguagePreference(args);
  const inferredNativeLanguage = args.lastResolvedLanguagePair?.nativeLanguage ?? inferNativeLanguage(args.input);

  return {
    nativeLanguage: effectiveNativeLanguagePreference === "auto" ? inferredNativeLanguage : effectiveNativeLanguagePreference,
    targetLanguage:
      args.targetLanguagePreference === "auto"
        ? inferTargetLanguage(inferredNativeLanguage, args.lastResolvedLanguagePair?.targetLanguage)
        : args.targetLanguagePreference,
  };
}

export function canSwapLanguagePreferences(args: {
  nativeLanguagePreference: LanguagePreference;
  targetLanguagePreference: LanguagePreference;
  followInputLanguage: boolean;
  lastResolvedLanguagePair: ResolvedLanguagePair | null;
}) {
  const effectiveNativeLanguagePreference = getEffectiveNativeLanguagePreference(args);

  if (effectiveNativeLanguagePreference !== "auto" && args.targetLanguagePreference !== "auto") {
    return true;
  }

  return Boolean(args.lastResolvedLanguagePair);
}

export function swapLanguagePreferences(args: {
  nativeLanguagePreference: LanguagePreference;
  targetLanguagePreference: LanguagePreference;
  followInputLanguage: boolean;
  lastResolvedLanguagePair: ResolvedLanguagePair | null;
}): {
  nativeLanguagePreference: LanguagePreference;
  targetLanguagePreference: LanguagePreference;
  followInputLanguage: boolean;
} {
  const effectiveNativeLanguagePreference = getEffectiveNativeLanguagePreference(args);

  if (effectiveNativeLanguagePreference !== "auto" && args.targetLanguagePreference !== "auto") {
    return {
      nativeLanguagePreference: args.targetLanguagePreference,
      targetLanguagePreference: effectiveNativeLanguagePreference,
      followInputLanguage: false,
    };
  }

  if (!args.lastResolvedLanguagePair) {
    return {
      nativeLanguagePreference: args.nativeLanguagePreference,
      targetLanguagePreference: args.targetLanguagePreference,
      followInputLanguage: args.followInputLanguage,
    };
  }

  // When users start in Auto, swap should still become deterministic after the first analysis instead of toggling back to Auto.
  return {
    nativeLanguagePreference: args.lastResolvedLanguagePair.targetLanguage,
    targetLanguagePreference: args.lastResolvedLanguagePair.nativeLanguage,
    followInputLanguage: false,
  };
}

function getEffectiveNativeLanguagePreference(args: {
  nativeLanguagePreference: LanguagePreference;
  followInputLanguage: boolean;
}): LanguagePreference {
  // The explicit toggle is clearer than surfacing "Auto" as a language, but the request layer still needs the same semantics.
  return args.followInputLanguage ? "auto" : args.nativeLanguagePreference;
}

function inferNativeLanguage(input: string): SupportedLanguageCode {
  if (JAPANESE_CHAR_PATTERN.test(input)) {
    return "ja";
  }

  if (KOREAN_CHAR_PATTERN.test(input)) {
    return "ko";
  }

  return "en";
}

function inferTargetLanguage(nativeLanguage: SupportedLanguageCode, lastResolvedTargetLanguage?: SupportedLanguageCode) {
  if (lastResolvedTargetLanguage) {
    return lastResolvedTargetLanguage;
  }

  return nativeLanguage === "en" ? "ja" : "en";
}
